import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DataTables from "../../Components/Tables/DataTable";
import { titleCase } from "../../utils/case";
import { getVenues, deleteVenue } from "../../api/venue.api";
import { Badge, Button } from "reactstrap";
import { Eye, Edit2, Trash2 } from "react-feather";
import { toast } from "react-toastify";

const VenueMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    perPage: 10,
    totalPages: 1,
  });

  // ✅ CENTRALIZED QUERY STATE
  const [query, setQuery] = useState({
    page: 1,
    perPage: 10,
    sortField: null,
    sortOrder: null,
  });

  // ✅ FETCH
  const fetchVenues = async () => {
    try {
      const res = await getVenues(query);

      setData(res?.data || []);
      setMeta(
        res?.meta || {
          total: 0,
          page: query.page,
          perPage: query.perPage,
          totalPages: 1,
        }
      );
    } catch (err) {
      console.error(err.message);
    }
  };

  // ✅ AUTO FETCH ON QUERY CHANGE
  useEffect(() => {
    fetchVenues();
  }, [query]);

  // ACTIONS
  const handleCreate = () => navigate("/venues/create");
  const handleView = (row) => navigate(`/venues/view/${row.id}`);
  const handleEdit = (row) => navigate(`/venues/edit/${row.id}`);

  const handleDelete = async (row) => {
    const confirmed = window.confirm(t("confirm delete"));
    if (!confirmed) return;

    try {
      setDeletingId(row.id);
      await deleteVenue(row.id);
      toast.success(t("deleted successfully"));

      fetchVenues();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  // ✅ PAGINATION
  const handlePageChange = (page) => {
    setQuery((prev) => ({ ...prev, page }));
  };

  const handlePerPageChange = (perPage, page) => {
    setQuery((prev) => ({ ...prev, perPage, page }));
  };

  // ✅ SORTING (CRITICAL)
  const handleSort = (column, sortDirection) => {
    setQuery((prev) => ({
      ...prev,
      page: 1,
      sortField: column.sortField,
      sortOrder: sortDirection,
    }));
  };

  const action = (
    <Button color="success" onClick={handleCreate}>
      {t("create").toUpperCase()}
    </Button>
  );

  const columns = [
    {
      name: "",
      cell: (row) =>
        row.photos ? (
          <img
            src={`https://bfiles.chinfumc.org.my/${row.photos}`}
            alt={row.name}
            style={{
              width: 50,
              height: 50,
              objectFit: "cover",
              borderRadius: 6,
            }}
          />
        ) : (
          "-"
        ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: titleCase(t("name")),
      selector: (row) => row.name,
      sortable: true,
      sortField: "name", // ✅ REQUIRED
      wrap: true,
    },
    {
      name: <div className="text-center w-100">{titleCase(t("allow public booking"))}</div>,
      selector: (row) => row.allowPublicBooking,
      sortable: true,
      sortField: "allowPublicBooking",
      cell: (row) => (
        <div className="text-center w-100">
          <Badge color={row.allowPublicBooking ? "success" : "secondary"}>
            {titleCase(t(row.allowPublicBooking ? "on" : "off"))}
          </Badge>
        </div>
      ),
    },
    {
      name: <div className="text-center w-100">{titleCase(t("requires approval"))}</div>,
      selector: (row) => row.requiresApproval,
      sortable: true,
      sortField: "requiresApproval",
      cell: (row) => (
        <div className="text-center w-100">
          <Badge color={row.requiresApproval ? "success" : "secondary"}>
            {titleCase(t(row.requiresApproval ? "on" : "off"))}
          </Badge>
        </div>
      ),
    },
    {
      name: <div className="text-center w-100">{titleCase(t("requires payment"))}</div>,
      selector: (row) => row.requiresPayment,
      sortable: true,
      sortField: "requiresPayment",
      cell: (row) => (
        <div className="text-center w-100">
          <Badge color={row.requiresPayment ? "success" : "secondary"}>
            {titleCase(t(row.requiresPayment ? "on" : "off"))}
          </Badge>
        </div>
      ),
    },
    {
      name: titleCase(t("pricing details")),
      selector: (row) => row.cost,
      sortable: true,
      sortField: "cost",
      cell: (row) => {
        if (!row.requiresPayment) return "-";

        const cost = Number(row.cost || 0).toFixed(2);
        const peakCost = Number(row.peakCost || 0).toFixed(2);

        return (
          <div>
            <div>RM{cost} ({row.chargedBy})</div>
            {Number(peakCost) > 0 && <div>Peak: RM{peakCost}</div>}
          </div>
        );
      },
    },
    {
      name: <div className="text-center w-100">{titleCase(t("status"))}</div>,
      selector: (row) => row.status,
      sortable: true,
      sortField: "status",
      cell: (row) => (
        <div className="text-center w-100">
          <Badge color={row.status === "active" ? "success" : "danger"}>
            {titleCase(t(row.status))}
          </Badge>
        </div>
      ),
    },
    {
      name: titleCase(t("action")),
      cell: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <Eye size={18} onClick={() => handleView(row)} style={{ cursor: "pointer", color: "blue" }} />
          <Edit2 size={18} onClick={() => handleEdit(row)} style={{ cursor: "pointer", color: "orange" }} />
          <Trash2
            size={18}
            style={{
              cursor: deletingId === row.id ? "not-allowed" : "pointer",
              color: "red",
              opacity: deletingId === row.id ? 0.5 : 1,
            }}
            onClick={() => handleDelete(row)}
          />
        </div>
      ),
    },
  ];

  return (
    <DataTables
      parent={titleCase(t("venue"))}
      title={titleCase(t("venue management"))}
      mainTitle={titleCase(t("venue management"))}
      headerCardTitle={titleCase(t("venue list"))}
      initialData={data}
      columns={columns}
      action={action}
      meta={meta}
      onPageChange={handlePageChange}
      onPerPageChange={handlePerPageChange}
      onSort={handleSort}
      sortServer
    />
  );
};

export default VenueMain;